<script setup lang="ts">
// The propose form is GENERATED from `service/actionCatalogue`, not hand-wired per action.
//
// Three things follow from the descriptor and nothing else: which actions the chosen platform can
// even offer, which anchor field is asked for, and which payload inputs exist and how they are
// typed. Adding an action to the catalogue therefore adds a working form with no change here — the
// shape that let this surface sit at 2 of 26 actions was one `v-if` per field.
import { computed } from 'vue'

import type {
  PlatformActionPlatform,
  PlatformActionType,
  ProposeActionInput,
} from '../models/platformAction.types'
import type { PayloadField } from '../service/actionCatalogue'
import { actionTypeKey, camelKey } from '../service/actionLabels'
import {
  actionsForPlatform,
  anchorFor,
  fieldsFor,
  isFieldRequired,
  needsTargetId,
  PLATFORM_OPTIONS,
  type ProposeErrors,
  type ProposeFieldError,
} from '../service/proposeInput'

import { useI18n } from '@/shared/i18n'
import { Input, Select, Textarea, type SelectOption } from '@/shared/ui'

const model = defineModel<ProposeActionInput>({ required: true })

const props = defineProps<{
  errors: ProposeErrors
  disabled?: boolean
}>()

const { t } = useI18n()

const platformOptions = computed<SelectOption[]>(() =>
  PLATFORM_OPTIONS.map((value) => ({
    value,
    label: t(`platformActions.platform.${value}`),
    meta: { visual: { platform: value } },
  })),
)

/** Only what this platform implements: anything else is a `422 UNKNOWN_ACTION` waiting to happen. */
const actionTypeOptions = computed<SelectOption[]>(() =>
  actionsForPlatform(model.value.platform).map((value) => ({
    value,
    label: t(`platformActions.actionType.${actionTypeKey(value)}`),
  })),
)

const fields = computed(() => fieldsFor(model.value.actionType, model.value.platform))
const anchorsOnCampaign = computed(() => anchorFor(model.value.actionType) === 'campaign')
const wantsTargetId = computed(() => needsTargetId(model.value.actionType))

function fieldLabel(field: PayloadField): string {
  return t(`platformActions.field.${camelKey(field.key)}`)
}

/** The wire value is the option's value, never its i18n key — `key-format-style` wants camelCase. */
function optionsFor(field: PayloadField): SelectOption[] {
  return (field.options ?? []).map((value) => ({
    value,
    label: t(`platformActions.enumValue.${camelKey(value)}`),
  }))
}

function errorText(problem: ProposeFieldError | undefined): string {
  return problem ? t(`platformActions.validation.${problem}`) : ''
}

function patch(change: Partial<ProposeActionInput>): void {
  model.value = { ...model.value, ...change }
}

function setField(key: string, value: unknown): void {
  patch({ payload: { ...model.value.payload, [key]: String(value ?? '') } })
}

/**
 * Switching platform or action changes which fields exist, and a stale value from the previous one
 * would be sent silently. The payload is dropped rather than merged for that reason.
 */
function selectPlatform(value: unknown): void {
  const platform = String(value ?? 'google') as PlatformActionPlatform
  const actions = actionsForPlatform(platform)
  const actionType = (
    actions.includes(model.value.actionType) ? model.value.actionType : actions[0]
  ) as PlatformActionType
  patch({ platform, actionType, payload: {} })
}

function selectAction(value: unknown): void {
  patch({ actionType: String(value ?? '') as PlatformActionType, payload: {} })
}
</script>

<template>
  <div class="propose-form flex flex-col gap-md">
    <Select
      :model-value="model.platform"
      :options="platformOptions"
      :label="t('platformActions.propose.platform')"
      :disabled="props.disabled ?? false"
      @change="selectPlatform"
    />

    <Select
      :model-value="model.actionType"
      :options="actionTypeOptions"
      :label="t('platformActions.propose.actionType')"
      :disabled="props.disabled ?? false"
      @change="selectAction"
    />

    <Input
      v-if="anchorsOnCampaign"
      :model-value="model.campaignId"
      :label="t('platformActions.propose.campaignId')"
      :placeholder="t('platformActions.propose.campaignIdHint')"
      :error="Boolean(props.errors.campaignId)"
      :error-message="errorText(props.errors.campaignId)"
      :disabled="props.disabled ?? false"
      @update:model-value="patch({ campaignId: String($event ?? '') })"
    />

    <Input
      v-else
      :model-value="model.adAccountId"
      :label="t('platformActions.propose.adAccountId')"
      :placeholder="t('platformActions.propose.adAccountIdHint')"
      :disabled="props.disabled ?? false"
      @update:model-value="patch({ adAccountId: String($event ?? '') })"
    />

    <Input
      v-if="wantsTargetId"
      :model-value="model.targetId"
      :label="t('platformActions.propose.targetId')"
      :placeholder="t('platformActions.propose.targetIdHint')"
      :error="Boolean(props.errors.targetId)"
      :error-message="errorText(props.errors.targetId)"
      :disabled="props.disabled ?? false"
      @update:model-value="patch({ targetId: String($event ?? '') })"
    />

    <template v-for="field in fields" :key="field.key">
      <Select
        v-if="field.type === 'enum'"
        :model-value="model.payload[field.key] ?? ''"
        :options="optionsFor(field)"
        :label="fieldLabel(field)"
        :disabled="props.disabled ?? false"
        @change="setField(field.key, $event)"
      />

      <Textarea
        v-else-if="field.type === 'json'"
        :model-value="model.payload[field.key] ?? ''"
        :label="fieldLabel(field)"
        :rows="4"
        :error="Boolean(props.errors.payload[field.key])"
        :error-message="errorText(props.errors.payload[field.key])"
        :disabled="props.disabled ?? false"
        @update:model-value="setField(field.key, $event)"
      />

      <Input
        v-else
        :model-value="model.payload[field.key] ?? ''"
        :type="field.type === 'number' || field.type === 'nullableNumber' ? 'number' : 'text'"
        step="any"
        :label="fieldLabel(field)"
        :placeholder="
          field.type === 'nullableNumber'
            ? t('platformActions.propose.removesCeilingHint')
            : field.type === 'list'
              ? t('platformActions.propose.listHint')
              : ''
        "
        :error="Boolean(props.errors.payload[field.key])"
        :error-message="errorText(props.errors.payload[field.key])"
        :disabled="props.disabled ?? false"
        @update:model-value="setField(field.key, $event)"
      />

      <p
        v-if="!isFieldRequired(field, model.platform)"
        class="propose-form__optional mt-[calc(var(--ui-space-md)_*_-1_+_var(--ui-space-2xs))] mx-0 mb-0 text-muted text-[0.75rem]"
      >
        {{ t('platformActions.propose.optional', { field: fieldLabel(field) }) }}
      </p>
    </template>

    <Input
      v-if="anchorsOnCampaign"
      :model-value="model.marketingChannel"
      :label="t('platformActions.propose.marketingChannel')"
      :placeholder="t('platformActions.propose.marketingChannelHint')"
      :disabled="props.disabled ?? false"
      @update:model-value="patch({ marketingChannel: String($event ?? '') })"
    />
  </div>
</template>
